import React, { useState, useEffect } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { useLanguage } from "@/contexts/LanguageContext";
import ScheduleCalendar from "./components/ScheduleCalendar";
import HolidayManager from "./components/HolidayManager";
import TeamStatCards from "./components/TeamStatCards";
import GuardWorkingHours from "./components/GuardWorkingHours";
import { useToast } from "@/components/ui/use-toast";
import { Calendar, Users, Database, Upload, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/contexts/UserContext";
import { useManagerPermissions } from "@/hooks/useManagerPermissions";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import BackupRestoreDialog from "./components/BackupRestoreDialog";
import { SendScheduleDialog } from "./components/SendScheduleDialog";
import { useSchedules } from "./hooks/useSchedules";
import { useGuards } from "./hooks/useGuards";
import { ColorIntensityProvider } from "./contexts/ColorIntensityContext";
import ColorIntensitySlider from "./components/ColorIntensitySlider";
import CalendarZoomSlider from "./components/CalendarZoomSlider";
import { useColorIntensity } from "./contexts/ColorIntensityContext";
const SchedulingPageContent = () => {
  const {
    t
  } = useLanguage();
  const { intensity, setIntensity, calendarZoom, setCalendarZoom } = useColorIntensity();
  const {
    toast
  } = useToast();
  const {
    isAdmin
  } = useUser();
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("calendar");
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
  const [currentDateRange, setCurrentDateRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [isInformingEmployees, setIsInformingEmployees] = useState(false);
  const [isSendScheduleDialogOpen, setIsSendScheduleDialogOpen] = useState(false);

  // Check manager permissions for the selected team
  const {
    canManageTeam
  } = useManagerPermissions(selectedTeamId);

  // Determine if user has admin-like permissions (either admin or manager of this team)
  const hasAdminLikePermissions = isAdmin || canManageTeam;

  // Fetch schedules and guards data
  const {
    schedules
  } = useSchedules(selectedTeamId);
  const {
    availableGuards: guards,
    allGuards
  } = useGuards(selectedTeamId);
  const handleTeamChange = (teamId: string) => {
    setSelectedTeamId(teamId);
    toast({
      title: t("teamScheduleLoaded"),
      description: t("viewingTeamSchedule")
    });
  };
  const handleCreateBackup = async () => {
    if (!selectedTeamId) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("selectTeamFirst")
      });
      return;
    }
    setIsBackingUp(true);
    try {
      const {
        data: schedules,
        error
      } = await supabase.from('team_schedules').select('*').eq('team_id', selectedTeamId);
      if (error) {
        throw new Error(error.message);
      }
      const {
        data,
        error: backupError
      } = await supabase.functions.invoke('create_schedule_backup', {
        body: {
          p_team_id: selectedTeamId,
          p_schedules: schedules,
          p_version: '1.0'
        }
      });
      if (backupError) {
        throw new Error(backupError.message);
      }
      toast({
        title: t("success"),
        description: t("backupCreatedOnline")
      });
    } catch (error) {
      console.error('Backup error:', error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("backupError")
      });
    } finally {
      setIsBackingUp(false);
    }
  };
  const openRestoreDialog = () => {
    if (!selectedTeamId) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("selectTeamFirst")
      });
      return;
    }
    setIsRestoreDialogOpen(true);
  };
  const handleInformEmployees = async () => {
    if (!selectedTeamId) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("selectTeamFirst")
      });
      return;
    }
    setIsInformingEmployees(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('send-schedule-notifications', {
        body: {
          team_id: selectedTeamId,
          current_date: new Date().toISOString().split('T')[0],
          notify_all_teams: false,
          include_change_logs: true,
          changes_only: true // Enable changes only mode for 24-hour changes
        }
      });
      if (error) {
        throw new Error(error.message);
      }
      toast({
        title: t("success"),
        description: `Αποστολή αλλαγών 24ώρου σε ${data.notified_employees} υπαλλήλους για ${data.total_changes} αλλαγές προγράμματος`
      });
    } catch (error) {
      console.error('Notification error:', error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("notificationError")
      });
    } finally {
      setIsInformingEmployees(false);
    }
  };
  const handleSendSchedule = () => {
    if (!selectedTeamId) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("selectTeamFirst")
      });
      return;
    }
    setIsSendScheduleDialogOpen(true);
  };
  return <AppLayout>
      <div className="flex flex-col h-full w-full">
        {/* Main content container with flex-1 to take available space */}
        <div className="flex-1 overflow-y-auto pb-4">
          <div className="flex flex-col gap-2 sm:gap-3 px-2 sm:px-3 md:px-4 lg:px-6 pt-2 sm:pt-3 md:pt-6">
            <div>
              <h1 className="text-xl md:text-2xl font-bold tracking-tight">{t("schedule")}</h1>
              <p className="text-sm md:text-base text-muted-foreground">{t("scheduleDescription")}</p>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
              {hasAdminLikePermissions && selectedTeamId && <div className="flex items-center gap-2 ml-auto flex-wrap">
                  

                  <Button variant="default" size="sm" onClick={handleSendSchedule} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
                    <Mail className="h-4 w-4" />
                    <span className="text-xs sm:text-sm">
                      Send Schedule
                    </span>
                  </Button>
                  
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="flex items-center gap-2">
                        <Database className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">{t("backupOptions")}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleCreateBackup} disabled={isBackingUp}>
                        <Database className="h-4 w-4 mr-2" />
                        {isBackingUp ? t("creatingBackup") : t("createBackup")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={openRestoreDialog}>
                        <Upload className="h-4 w-4 mr-2" />
                        {t("restoreBackup")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>}
            </div>
            
            <TeamStatCards selectedTeamId={selectedTeamId} onTeamChange={handleTeamChange} />
            
            {selectedTeamId && (
              <div className="flex gap-4 max-w-2xl">
                <div className="flex-1">
                  <ColorIntensitySlider value={intensity} onChange={setIntensity} />
                </div>
                <div className="flex-1">
                  <CalendarZoomSlider value={calendarZoom} onChange={setCalendarZoom} />
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            {hasAdminLikePermissions && <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full px-2 sm:px-3 md:px-4 lg:px-6">
                <TabsList className="grid grid-cols-2 w-full max-w-[400px]">
                  <TabsTrigger value="calendar">
                    <Calendar className="h-4 w-4 mr-2" />
                    {t("schedule") || "Schedule"}
                  </TabsTrigger>
                  <TabsTrigger value="holidays">
                    <Calendar className="h-4 w-4 mr-2" />
                    {t("holidays") || "Holidays"}
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="calendar" className="mt-2 sm:mt-4">
                  {selectedTeamId ? <div className="space-y-4 sm:space-y-6">
                      <ScheduleCalendar teamId={selectedTeamId} calendarZoom={calendarZoom} onDateRangeChange={range => setCurrentDateRange(range)} />
                    </div> : <div className="h-64 flex items-center justify-center">
                      {/* Empty space instead of the info box */}
                    </div>}
                </TabsContent>
                
                <TabsContent value="holidays" className="mt-2 sm:mt-4">
                  <HolidayManager />
                </TabsContent>
              </Tabs>}
            
            {!hasAdminLikePermissions && <div className="px-2 sm:px-3 md:px-4 lg:px-6">
                {selectedTeamId ? <div className="space-y-4 sm:space-y-6">
                    <ScheduleCalendar teamId={selectedTeamId} calendarZoom={calendarZoom} onDateRangeChange={range => setCurrentDateRange(range)} />
                  </div> : <div className="h-64 flex items-center justify-center">
                    {/* Empty space instead of the info box */}
                  </div>}
              </div>}
          </div>
        </div>
        
        {selectedTeamId && <div className="w-full px-2 sm:px-3 md:px-4 lg:px-6 bg-background border-t border-border">
            <div className="py-2 sm:py-4">
              <GuardWorkingHours events={schedules as any} guards={guards} userIsAdmin={hasAdminLikePermissions} teamId={selectedTeamId} dateRange={currentDateRange || undefined} />
            </div>
          </div>}
        
        {isRestoreDialogOpen && selectedTeamId && <BackupRestoreDialog teamId={selectedTeamId} isOpen={isRestoreDialogOpen} onClose={() => setIsRestoreDialogOpen(false)} onRestoreComplete={() => {
        toast({
          title: t("success"),
          description: t("restoreCompleted")
        });
      }} />}
        
        <SendScheduleDialog open={isSendScheduleDialogOpen} onOpenChange={setIsSendScheduleDialogOpen} selectedTeamId={selectedTeamId} />
      </div>
    </AppLayout>;
};

const SchedulingPage = () => {
  return (
    <ColorIntensityProvider>
      <SchedulingPageContent />
    </ColorIntensityProvider>
  );
};

export default SchedulingPage;